  class TestimonialSlider extends HTMLElement {
    constructor() {
      super();
      this.currentSlide = 0;
      this.slider = this.querySelector('.why-choose-us__slider');
      this.slides = this.querySelectorAll('.why-choose-us__card');
      this.dots = this.querySelectorAll('.why-choose-us__dot');
      this.totalSlides = this.slides.length;
      this.isAnimating = false;
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.autoplayInterval = null;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayDelay = parseInt(this.dataset.autoplayDelay) || 5000;
      this.scrollTimeout = null;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      if (this.totalSlides === 0) return;

      this.setupDots();
      this.setupInteractions();
      this.setupKeyboardNavigation();
      
      // Listen for scroll to update dots active state
      this.slider.addEventListener('scroll', () => {
        this.onScroll();
      }, { passive: true });
      
      // Start autoplay if enabled
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }

      // Update position on resize - we might want to re-snap
      window.addEventListener('resize', () => {
         // Browser handles snap, but we can ensure alignment
         this.updateSlidePosition();
      });
    }

    onScroll() {
       // Ignore scroll events during automated animations to prevent dot flickering
       if (this.isAnimating) return;

       // Simple debounce to prevent excessive updates
       if (this.scrollTimeout) return;
       
       this.scrollTimeout = setTimeout(() => {
         const scrollLeft = this.slider.scrollLeft;
         const scrollWidth = this.slider.scrollWidth;
         const clientWidth = this.slider.clientWidth;
         
         // Check if we are at the very end (allowing for small rounding errors)
         // If so, force the last slide to be active
         if (Math.ceil(scrollLeft + clientWidth) >= scrollWidth) {
            if (this.currentSlide !== this.totalSlides - 1) {
                this.currentSlide = this.totalSlides - 1;
                this.updateDots();
            }
            this.scrollTimeout = null;
            return;
         }
         
         // Find closest slide
         let minDiff = Infinity;
         let closestIndex = 0;
         
         this.slides.forEach((slide, index) => {
           // We compare distance to the left edge
           const diff = Math.abs(slide.offsetLeft - scrollLeft);
           if (diff < minDiff) {
             minDiff = diff;
             closestIndex = index;
           }
         });
         
         if (this.currentSlide !== closestIndex) {
           this.currentSlide = closestIndex;
           this.updateDots();
         }
         this.scrollTimeout = null;
       }, 50);
    }
    
    setupInteractions() {
        const pauseEvents = ['mousedown', 'touchstart', 'mouseenter', 'focus'];
        
        pauseEvents.forEach(evt => {
            this.addEventListener(evt, () => this.pauseAutoplay(), { passive: true });
        });

        this.addEventListener('mouseleave', () => {
             if(this.autoplayEnabled) this.startAutoplay();
        });
    }

    setupDots() {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.pauseAutoplay(); // Pause on manual interaction
          this.goToSlide(index);
        });
      });
    }

    setupKeyboardNavigation() {
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.pauseAutoplay();
          this.previousSlide();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.pauseAutoplay();
          this.nextSlide();
        }
      });
    }

    goToSlide(index) {
      if (index < 0 || index >= this.totalSlides) return;
      
      this.isAnimating = true;
      this.currentSlide = index;
      this.updateSlidePosition();
      this.updateDots();
      
      // Unlock after transition
      setTimeout(() => {
        this.isAnimating = false;
      }, 600);
    }

    nextSlide() {
      const nextIndex = (this.currentSlide + 1) % this.totalSlides;
      this.goToSlide(nextIndex);
    }

    previousSlide() {
      const prevIndex = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
      this.goToSlide(prevIndex);
    }

    updateSlidePosition() {
      if (!this.slides.length) return;
      
      // We use the slide's offsetLeft to find its position.
      // The browser automatically clamps values, so we don't need manual math 
      // to check if we are going too far. It won't scroll past the end.
      const slide = this.slides[this.currentSlide];
      const targetScroll = slide.offsetLeft;

      this.slider.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }

    updateDots() {
      this.dots.forEach((dot, index) => {
        const isActive = index === this.currentSlide;
        dot.classList.toggle('why-choose-us__dot--active', isActive);
        dot.setAttribute('aria-selected', isActive);
      });
    }

    startAutoplay() {
      // Clear any existing interval
      this.pauseAutoplay();
      
      this.autoplayInterval = setInterval(() => {
        this.nextSlide();
      }, this.autoplayDelay);
    }

    pauseAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }

    disconnectedCallback() {
      this.pauseAutoplay();
    }
  }

  customElements.define('testimonial-slider', TestimonialSlider);